export type CoverageRingProps = {
  value: number;
  total: number;
  /** Optional label inside the ring. Defaults to `${pct}%` */
  label?: string;
  /** Diameter in px (defaults to 40 to match h-10 w-10) */
  size?: number;
  /** Stroke thickness in px */
  strokeWidth?: number;
  /** Tailwind stroke class for progress (e.g. "stroke-red-500") */
  progressClassName?: string;
  /** Tailwind stroke class for track (e.g. "stroke-border") */
  trackClassName?: string;
};

export const CoverageRing: React.FC<CoverageRingProps> = ({
  value,
  total,
  label,
  size = 40,
  strokeWidth = 4,
  progressClassName = 'stroke-primary', // hardcode for now; swap later
  trackClassName = 'stroke-border',
}) => {
  const pct =
    total > 0
      ? Math.min(100, Math.max(0, Math.round((value / total) * 100)))
      : 0;

  // Geometry
  const r = (size - strokeWidth) / 2;
  const c = 2 * Math.PI * r;
  const dashOffset = c * (1 - pct / 100);

  const centerLabel = label ?? `${pct}%`;

  return (
    <div
      className="relative shrink-0"
      style={{ width: size, height: size }}
      aria-label={`Coverage ${pct}% (${value} of ${total})`}
      role="img"
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="block"
      >
        {/* Track */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          strokeWidth={strokeWidth}
          className={trackClassName}
        />

        {/* Progress */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={dashOffset}
          className={progressClassName}
          // Start at 12 o'clock
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>

      {/* Center label */}
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-xs font-medium tabular-nums text-foreground">
          {centerLabel}
        </span>
      </div>
    </div>
  );
};
